import {ErrorMessage, Field, Form, Formik} from "formik";
import postData from "../services/postData";
import Modal from "./Modal";
import {useState, useRef, useEffect} from "react";
import {ScrollToFieldError} from "./formik/ScrollToFieldError";
import {formSchema} from "../schema/formSchema";
import SkodaSecondDataInfo from "./SkodaSecondDataInfo";
import SkodaDataInfo from "./SkodaDataInfo";
import SkodaConsentDataInfo from "./SkodaConsentDataInfo";
import SkodaConsentSecondDataInfo from "./SkodaConsentSecondDataInfo";
import DealerMap from "./DealerMap";
import SkodaCarMarks, { carMarks } from "./SkodaCarMarks";

// Funkcja do kodowania nazwy modelu w URL-safe format
const encodeCarModel = (modelName) => {
    return encodeURIComponent(modelName.replace(/\s+/g, '-').toLowerCase());
};

// Funkcja do dekodowania nazwy modelu z URL
const decodeCarModel = (encodedModel) => {
    return decodeURIComponent(encodedModel).replace(/-/g, ' ');
};

// Funkcja do znajdowania modelu po nazwie
const findCarByEncodedName = (encodedName) => {
    const decodedName = decodeCarModel(encodedName);
    return carMarks.find(car => 
        car.name.toLowerCase() === decodedName || 
        car.name.toLowerCase().replace(/\s+/g, '-') === decodedName
    );
};

export default function ServiceForm({setLoadingSpinnerOpen}) {
    const [selectedDealer, setSelectedDealer] = useState(null);
    const [showCarMarks, setShowCarMarks] = useState(false);
    const [selectedCar, setSelectedCar] = useState(null);
    const formikRef = useRef(null);

    const formType = 'newsletter,elroq,2024';

    const [successModal, setSuccessModal] = useState(false);
    const [failureModal, setFailureModal] = useState(false);
    const [formIsSubmitting, setFormIsSubmitting] = useState(false);

    // Funkcja do aktualizacji URL z wybranym modelem
    const updateURLWithCarModel = (car) => {
        if (car) {
            const encodedModel = encodeCarModel(car.name);
            
            // Aktualizuj query parameters zamiast hash
            const url = new URL(window.location);
            url.searchParams.set('model', encodedModel);
            
            // Usuń hash jeśli istnieje
            if (window.location.hash.includes('model=')) {
                window.location.hash = '';
            }
            
            // Aktualizuj URL bez przeładowania strony
            window.history.replaceState({}, '', url.toString());
        } else {
            // Usuń parametr model z URL
            const url = new URL(window.location);
            url.searchParams.delete('model');
            
            // Usuń hash jeśli istnieje
            if (window.location.hash.includes('model=')) {
                window.location.hash = '';
            }
            
            // Aktualizuj URL bez przeładowania strony
            window.history.replaceState({}, '', url.toString());
        }
    };

    // Funkcja do odczytywania modelu z URL przy ładowaniu
    const loadCarFromURL = () => {
        // Sprawdź hash URL
        const hash = window.location.hash;
        const hashModelMatch = hash.match(/model=([^&]+)/);
        
        // Sprawdź query parameters
        const urlParams = new URLSearchParams(window.location.search);
        const queryModel = urlParams.get('model');
        
        let encodedModel = null;
        
        if (hashModelMatch) {
            encodedModel = hashModelMatch[1];
        } else if (queryModel) {
            encodedModel = queryModel;
        }
        
        if (encodedModel) {
            const car = findCarByEncodedName(encodedModel);
            
            if (car) {
                setSelectedCar(car);
                // Aktualizacja wartości w formularzu Formik
                if (formikRef.current) {
                    formikRef.current.setFieldValue('selectedCar', car.name);
                }
                // Wyślij dane do analityki o załadowanym modelu z URL
                sendModelSelectionToAnalytics(car);
                return true;
            }
        }
        return false;
    };

    // useEffect do odczytywania modelu z URL przy ładowaniu komponentu
    useEffect(() => {
        loadCarFromURL();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Nasłuchiwanie zmian w URL
    useEffect(() => {
        const handleURLChange = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const queryModel = urlParams.get('model');
            const hash = window.location.hash;
            const hashModelMatch = hash.match(/model=([^&]+)/);
            
            // Jeśli nie ma modelu ani w query ani w hash, wyczyść wybór
            if (!queryModel && !hashModelMatch) {
                setSelectedCar(null);
                if (formikRef.current) {
                    formikRef.current.setFieldValue('selectedCar', '');
                }
            }
        };

        window.addEventListener('popstate', handleURLChange);
        return () => window.removeEventListener('popstate', handleURLChange);
    }, []);

    const handleWybierzClick = () => {
        setShowCarMarks(true);
    };

    const handleCloseCarMarks = () => {
        setShowCarMarks(false);
    };

    const handleCarSelect = (car) => {
        setSelectedCar(car);
        setShowCarMarks(false);
        // Aktualizacja wartości w formularzu Formik
        formikRef.current?.setFieldValue('selectedCar', car.name);
        // Aktualizacja URL z wybranym modelem
        updateURLWithCarModel(car);
        // Wyślij dane do analityki
        sendModelSelectionToAnalytics(car);
    };

    // Funkcja do czyszczenia wyboru modelu
    const handleClearCarSelection = () => {
        setSelectedCar(null);
        // Aktualizacja wartości w formularzu Formik
        formikRef.current?.setFieldValue('selectedCar', '');
        // Usuń model z URL
        updateURLWithCarModel(null);
    };

    // Funkcja do generowania linku z konkretnym modelem (przydatne do remarketingu)
    const generateModelLink = (carName) => {
        const encodedModel = encodeCarModel(carName);
        const url = new URL(window.location);
        url.searchParams.set('model', encodedModel);
        return url.toString();
    };

    // Eksportuj funkcję do użytku zewnętrznego (np. do remarketingu)
    window.generateModelLink = generateModelLink;
    
    // Funkcja do sprawdzania aktualnego modelu w URL (przydatne do remarketingu)
    const getCurrentModelFromURL = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const queryModel = urlParams.get('model');
        const hash = window.location.hash;
        const hashModelMatch = hash.match(/model=([^&]+)/);
        
        if (queryModel) {
            return decodeCarModel(queryModel);
        } else if (hashModelMatch) {
            return decodeCarModel(hashModelMatch[1]);
        }
        return null;
    };
    
    // Eksportuj funkcję do sprawdzania aktualnego modelu
    window.getCurrentModelFromURL = getCurrentModelFromURL;
    
    // Funkcja do wysyłania danych o wybranym modelu do systemów remarketingu
    const sendModelSelectionToAnalytics = (car) => {
        if (car && window.dataLayer) {
            window.dataLayer.push({
                'event': 'model_selection',
                'model_name': car.name,
                'model_encoded': encodeCarModel(car.name),
                'page_url': window.location.href
            });
        }
    };

    const dataLayerPush = (orderId, leadId) => {
        try {
            window.dataLayer.push({
                'event': 'formularze',
                'wypelniony-formularz': 'oferta-zamowiono',
                'orderid': orderId,
                'leadid': leadId
            });
            console.log('dataLayer.push ok:', orderId);
        } catch (err) {
            console.error('dataLayer.push error:', err);
        }
    };

    const syneriseSendFormData = (orderId, values) => {
        if (!('SR' in window)) {
            console.error('SR undefined');
            return;
        }

        const snrData = {
            orderID: orderId,
            firstname: values.firstName, //imie
            email: values.email, //email
            ...(values.newsletterAgreement ? {newsletterAgreement: 'enabled'} : {}), // brak zgody => atrybut w ogóle nie wysyłany (zgodnie z dokumentacją wdrożeniową)
            newsletter_info: values.newsletter_info,
            statute: values.statute,
            selectedCar: selectedCar ? selectedCar.name : null,
            selectedDealer: selectedDealer ? selectedDealer.Name : null,
            dealerAddress: selectedDealer ? `${selectedDealer.ZipCode} ${selectedDealer.City}, ${selectedDealer.Street}` : null,
        };
        try {
            window.SR.event.sendFormData(`${selectedCar.slug},jazda-probna,bp`, snrData)
                .then((res) => {
                    console.log(formType);
                    console.log(res);
                })
                .catch((err) => {
                    console.log(formType);
                    console.error(err);
                });
        } catch (err) {
            console.error('SR error', err);
        }
    };

    const insertSyneriseScript = () => {
        const syneriseScript = document.createElement('script');
        syneriseScript.innerHTML = `
            function onSyneriseLoad() {
                SR.init({
                    'trackerKey':'F79A2C6D-7D1D-2E3A-F1D4-997A40970066',
                });
            }

            (function(s,y,n,e,r,i,se){s['SyneriseObjectNamespace']=r;s[r]=s[r]||[],s[r]._t=1*new Date(),
                s[r]._i=0,s[r]._l=i;var z=y.createElement(n),se=y.getElementsByTagName(n)[0];z.async=1;z.src=e;
                se.parentNode.insertBefore(z,se);z.onload=z.onreadystatechange=function(){var rdy=z.readyState;
                    if(!rdy||/complete|loaded/.test(z.readyState)){s[i]();z.onload = null;z.onreadystatechange=null;}};
            })(window,document,'script','//www.snrcdn.net/sdk/3.0/synerise-javascript-sdk.min.js','SR', 'onSyneriseLoad');
        `;
        document.body.appendChild(syneriseScript);
    }

    return (
        <Formik
            initialValues={{
                firstName: '',
                phone: '',
                email: '',
                preferredContactTime: '',
                dealerMap: '',
                selectedCar: '',
                newsletterViaEmail: false,
                newsletterViaMessage: false,
                newsletterViaPhone: false,
                newsletterReceiving: false,
                newsletterAgreement: false,
                newsletter_info: false,
                statute: false,
            }}
            validationSchema={formSchema}
            onSubmit={(values, actions) => {
                actions.setSubmitting(false);
                setFormIsSubmitting(true);

                if(window.SR === undefined) {
                    insertSyneriseScript();
                }
                // Add selected car and dealer data to form values
                const formDataWithCar = {
                    ...values,
                    selectedCar: selectedCar ? selectedCar.name : null,
                    dealerName: selectedDealer ? selectedDealer.Name : null,
                    dealerLocation: selectedDealer ? `${selectedDealer.ZipCode} ${selectedDealer.City}, ${selectedDealer.Street}` : null,
                    dealerId: selectedDealer ? selectedDealer.GlobalID : null,
                };

                const intervalId = setInterval(() => {
                    if ((window.SR !== undefined)) {
                        clearInterval(intervalId);

                        postData(formDataWithCar)
                            .then((data) => {
                                if (data && data.status === 'ok') {
        
                                    dataLayerPush(data.orderId, data.leadId);
                                    syneriseSendFormData(data.orderId, values);
                                    actions.resetForm();
                                    setSuccessModal(true);
                                } else {
                                    setFailureModal(true);
                                }
                            })
                            .catch((error) => {
                                setFailureModal(true);
                            })
                            .finally(() => {
                                setFormIsSubmitting(false);
                                // setLoadingSpinnerOpen(false);
                                window.scrollTo({
                                    top: 0,
                                    left: 0,
                                    behavior: "smooth"
                                });
                            });
                    }
                }, 300);

                // Set failure when no SR in window after 5s
                setTimeout(() => {
                    clearInterval(intervalId);
                    if (window.SR === undefined) {
                        setFailureModal(true);                    
                        setFormIsSubmitting(false);
                        window.scrollTo({
                            top: 0,
                            left: 0,
                            behavior: "smooth"
                        });
                        return;
                    }
                }, 5000);
            }}
        >
            {(formikProps) => {
                formikRef.current = formikProps;
                return (
                <>
                    <Modal open={successModal} setOpen={setSuccessModal}>
                        <b>Dziękujemy za wybór Škody</b>
                    </Modal>

                    <Modal open={failureModal} setOpen={setFailureModal}>
                        Wystąpił błąd!<br/>
                        Prosimy spróbować później.
                    </Modal>

                    <Form className="form-container form-container js-subscribe-form" method={`post`}>
                        <ScrollToFieldError/>
                            <div className="form-row">
                                <label className="form-label">WYBIERZ MODEL, KTÓRY CHCESZ PRZETESTOWAĆ*</label>
                                <div className="form-row form-row--buttons">
                                    {!selectedCar ? (
                                        <button 
                                            type="button"
                                            className="button button--primary" 
                                            onClick={handleWybierzClick}
                                            style={{width: '100%', marginBottom: '10px'}}
                                        >
                                            Wybierz model
                                        </button>
                                    ) : (
                                        <div style={{width: '100%', marginBottom: '10px'}}>
                                            <h4 style={{display: "inline-block", margin: 0}}>{selectedCar.name}</h4>
                                            <button 
                                                type="button"
                                                className="button button--primary" 
                                                style={{display: "inline-block", marginLeft: "1em"}} 
                                                onClick={handleWybierzClick}
                                            >
                                                Zmień
                                            </button>
                                            <p style={{margin: '10px 0 0 0'}} dangerouslySetInnerHTML={{ __html: selectedCar.desc }}></p>
                                            <img src={selectedCar.image} alt={selectedCar.name} style={{maxWidth: '100%', height: 'auto', marginTop: '10px'}} />
                                        </div>
                                    )}
                                </div>
                                <Field name="selectedCar" type="hidden" />
                                <ErrorMessage name="selectedCar">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>

                        <div className="form-fields">

                            <div className="form-row form-row--flex">
                                <label className="form-label" htmlFor="firstName">Imię</label>
                                <Field id="firstName" name="firstName" type="text" className="form-input" maxLength={50}></Field>
                                <ErrorMessage name="firstName">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>

                            <div className="form-row form-row--flex">
                                <label className="form-label" htmlFor="text">Telefon</label>
                                <Field id="phone" name="phone" type="text" className="form-input" maxLength={255}></Field>
                                <ErrorMessage name="phone">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>

                            <div className="form-row form-row--flex">
                                <label className="form-label" htmlFor="email">E-mail<br/><span className="form-label--optional">(opcjonalnie)</span></label>
                                <Field id="email" name="email" type="email" className="form-input" maxLength={255}></Field>
                                <ErrorMessage name="email">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>

                            <div className="form-row form-row--flex">
                                <label className="form-label" htmlFor="preferredContactTime">Preferowana<br/>pora kontaktu<br/><span className="form-label--optional">(opcjonalnie)</span></label>
                                <Field as="select" id="preferredContactTime" name="preferredContactTime" className="form-input">
                                    <option value="">Wybierz</option>
                                    <option value="9-13">9-13</option>
                                    <option value="13-16">13-16</option>
                                    <option value="16-20">16-20</option>
                                </Field>
                                <ErrorMessage name="preferredContactTime">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>
                        </div>


                        <div className="form-map">
                            <label className="form-label" htmlFor="dealerMap">DEALER:</label>
                            <DealerMap 
                                onDealerSelect={(dealer) => {
                                    setSelectedDealer(dealer);
                                    // Aktualizacja wartości w formularzu
                                    const dealerValue = `${dealer.Name}, ${dealer.ZipCode} ${dealer.City}, ${dealer.Street}`;
                                    formikRef.current?.setFieldValue('dealerMap', dealerValue);
                                }}
                                selectedDealer={selectedDealer}
                            />
                            <Field name="dealerMap" type="hidden" />
                            <ErrorMessage name="dealerMap">{msg => <div className="form-error">Wybierz dealer</div>}</ErrorMessage>
                        </div>

                        <div className="form-row form-row--buttons form-submit-wrapper">
                            <button type="submit" className="button button--primary button--send" disabled={formIsSubmitting}>Wyślij zgłoszenie</button>
                        </div>

                        <div className="form-consent">
                            <div className="form-row">
                                <div className="consent-scroll">
                                    <p>
                                        Pozostawienie przez Ciebie danych i wysłanie formularza oznacza Twoją zgodę dla Volkswagen Group Polska sp. z o.o. 
                                        oraz poprzez Twój wybór także dla wybranego Autoryzowanego Dealera marki Škoda [dane Dealera możesz znaleźć na stronie <a style={{color: "#0E3A2F"}} href="https://retailers.skoda-auto.com/456/pl-pl" target="_blank" rel="noreferrer"><b>https://retailers.skoda-auto.com/456/pl-pl</b></a>] 
                                        na skontaktowanie się z Tobą w celu przedstawienia Ci informacji na temat zakupu samochodu marki Skoda 
                                        oraz ogólnych informacji dot. możliwych sposobów finansowania z użyciem telefonu lub adresu e-mail. Skontaktujemy się z Tobą, zweryfikujemy 
                                        Twoje dane i potwierdzimy Twoje zainteresowanie informacjami na temat oferty. Następnie Twoje zapytanie przekażemy wybranemu przez 
                                        Ciebie Autoryzowanemu Dealerowi marki Škoda, który skontaktuje z Tobą celem obsługi Twojego zapytania (Preferowany Dealer). 
                                        Podanie Twoich danych jest dobrowolne, lecz niezbędne do obsługi Twojego zapytania. Administratorem danych jest 
                                        Volkswagen Group Polska sp. z o.o. z siedzibą w Poznaniu (61-037), ul. Krańcowa 44 wpisana do Rejestru Przedsiębiorców prowadzonego przez 
                                        Sąd Rejonowy w Poznaniu - Nowe Miasto i Wilda, VIII Wydział Gospodarczy pod numerem KRS 0000327143, NIP 782-24-63-563, REGON 301062169 (VGP) 
                                        oraz Preferowany Dealer, działający na zasadzie współadministrowania. W każdej chwili możesz cofnąć swoją zgodę i zrezygnować z otrzymania informacji dot. 
                                        oferty informując konsultanta Centrum Informacji lub Preferowanego Dealera o rezygnacji z dalszego kontaktu w tym zakresie (utrata zainteresowania ofertą 
                                        będzie dla nas jednoznaczna z cofnięciem Twojej zgody na obsługę tego zapytania). Swoje dodatkowe zgody możesz cofnąć poprzez formularz umieszczony na 
                                        stronie internetowej <a style={{color: "#0E3A2F"}} href="https://www.twoja.skoda.pl/formularze/formularz-cofniecia-zgod" target="_blank" rel="noreferrer"><b>https://www.twoja.skoda.pl/formularze/formularz-cofniecia-zgod</b></a> albo bezpośrednio w kontakcie z VGP lub z Autoryzowanym Dealerem / Autoryzowanym 
                                        Partnerem Serwisowym marki Škoda.
                                    </p>
                                </div>
                            </div>

                            <div className="form-row">
                                <p className="small-text"><b>Poniższe zgody są opcjonalne i dobrowolne, a ich udzielenie lub odmowa są niezależne od obsługi twojego zapytania.</b></p>
                            </div>

                            <div className="form-row">
                                <div className="form-checkbox">
                                    <Field className="form-check-input" id="newsletterReceiving" name="newsletterReceiving" type="checkbox"></Field>
                                    <label htmlFor="newsletterReceiving" className="form-check-label">
                                        Zgadzam się na używanie przez Volkswagen Group Polska Sp. z o.o. (VGP) oraz Škoda Auto a.s. przekazanych danych, 
                                        danych zebranych o moich zapytaniach i zainteresowaniach, ofertach, zamówieniach i zleceniach w ramach...
                                        <SkodaConsentDataInfo/>
                                    </label>
                                </div>
                                <ErrorMessage name="newsletterReceiving">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>

                            <div className="form-row">
                                <div className="form-checkbox">
                                    <span className="form-span">Chcę otrzymać od VGP w/w informacje handlowe i marketingowe poprzez:</span>

                                    <Field className="form-check-input" id="newsletterViaEmail" name="newsletterViaEmail" type="checkbox"></Field>
                                    <label htmlFor="newsletterViaEmail" className="form-check-label">E-mail</label>

                                    <Field className="form-check-input" id="newsletterViaMessage" name="newsletterViaMessage" type="checkbox"></Field>
                                    <label htmlFor="newsletterViaMessage" className="form-check-label">SMS/MMS</label>

                                    <Field className="form-check-input" id="newsletterViaPhone" name="newsletterViaPhone" type="checkbox"></Field>
                                    <label htmlFor="newsletterViaPhone" className="form-check-label">Telefon</label>
                                </div>
                                <ErrorMessage name="newsletterViaEmail">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>

                            <SkodaConsentSecondDataInfo/>
                            
                            <div className="form-row">
                                <div className="form-checkbox">
                                    <Field className="form-check-input" id="newsletterAgreement" name="newsletterAgreement" type="checkbox"></Field>
                                    <label htmlFor="newsletterAgreement" className="form-check-label">
                                        Chcę otrzymywać Newsletter od Volkswagen Group Polska Sp. z o.o. z wykorzystaniem adresu e-mail oraz pozostałych podanych przeze mnie Danych.                               </label>
                                </div>
                                <ErrorMessage name="newsletterAgreement">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>
                            <div className="form-row">
                                <div className="form-checkbox">
                                    <Field className="form-check-input" id="newsletter_info" name="newsletter_info" type="checkbox"></Field>
                                    <label htmlFor="newsletter_info" className="form-check-label">
                                        W ramach Newslettera zgadzam się na otrzymywanie od Volkswagen Group Polska Sp. z o.o. w/w informacji mogących stanowić informacje handlowe i marketingowe.                           </label>
                                </div>
                                <ErrorMessage name="newsletter_info">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>
                            <div className="form-row">
                                <div className="form-checkbox">
                                    <Field className="form-check-input" id="statute" name="statute" type="checkbox"></Field>
                                    <label htmlFor="statute" className="form-check-label">
                                        Akceptuję <a href="https://twoja.skoda.pl/brandportal/newsletter/rules/0afc6cb8156bd43640a6a5f47d5f655f4c45c2b1.pdf" target="_blank" rel="noreferrer">regulamin</a> newslettera
                                    </label>
                                </div>
                                <ErrorMessage name="statute">{msg => <div className="form-error">{msg}</div>}</ErrorMessage>
                            </div>
                            <SkodaSecondDataInfo/>
                            <SkodaDataInfo/>
                        </div>
                    </Form>
                    {showCarMarks && <SkodaCarMarks onClose={handleCloseCarMarks} onCarSelect={handleCarSelect} />}
                </>
            );
        }}
        </Formik>
    );
}
